// components/InvestmentPreferences/index.jsx
// Enhanced Investment Preferences with Project Match
// Version 2.1 - Pass existing deals to enrichment API for preference inference

import React, { useState, useCallback, useMemo } from 'react';
import { 
  Sparkles, RefreshCw, ChevronDown, ChevronRight, CheckCircle, MinusCircle, 
  AlertCircle, Globe, Zap, BarChart3, TrendingUp, Edit2, Save, X, 
  Plus, Trash2, ExternalLink, Clock, Building2, Target, Link2,
  MapPin, DollarSign, Calendar, ArrowUpRight, Loader2, Eye
} from 'lucide-react';
import { getIndustryConfig, getIndustryOptions } from '../../lib/industryConfigs';
import { authFetch } from '../../lib/authFetch';

// ============================================
// PROJECT MATCH ALGORITHM
// ============================================

function calculateProjectMatch(project, preferences, industryConfig) {
  if (!preferences || !preferences.dimensions) return null;
  
  let totalScore = 0;
  let totalWeight = 0;
  const matchDetails = [];

  // Helper to find dimension by key
  const getDimension = (key) => preferences.dimensions?.find(d => d.key === key);
  
  // 1. Technology/Category Match (weight: 30%)
  const techDim = getDimension('technologies') || getDimension('sectors') || getDimension('assetClasses');
  if (techDim?.values?.length > 0) {
    const projectTech = project.technology || project.category || '';
    const techValues = techDim.values.map(v => (v.value || v).toLowerCase());
    const projectTechLower = projectTech.toLowerCase();
    
    // Check for match
    const techMatch = techValues.some(t => 
      projectTechLower.includes(t) || t.includes(projectTechLower)
    );
    
    if (techMatch) {
      const confidence = techDim.values.find(v => 
        projectTechLower.includes((v.value || v).toLowerCase())
      )?.confidence || 0.7;
      totalScore += 30 * confidence;
      matchDetails.push({ factor: 'Technology', matched: true, score: Math.round(30 * confidence) });
    }
    totalWeight += 30;
  }

  // 2. Geography Match (weight: 25%)
  const geoDim = getDimension('geographies');
  if (geoDim?.values?.length > 0 && project.location) {
    const projectLoc = project.location.toLowerCase();
    const geoMatch = geoDim.values.find(g => {
      const geoName = (g.value || g.region || g).toLowerCase();
      return projectLoc.includes(geoName) || geoName.includes(projectLoc.split(',')[0].trim());
    });
    
    if (geoMatch && (geoMatch.status === 'active' || geoMatch.status === 'expanding')) {
      const geoScore = geoMatch.status === 'expanding' ? 1.0 : 0.8;
      totalScore += 25 * geoScore * (geoMatch.confidence || 0.7);
      matchDetails.push({ factor: 'Geography', matched: true, score: Math.round(25 * geoScore) });
    }
    totalWeight += 25;
  }

  // 3. Stage Match (weight: 20%)
  const stageDim = getDimension('dealStage') || getDimension('stage');
  if (stageDim?.values?.length > 0 && project.stage) {
    const projectStage = project.stage.toLowerCase();
    const stageValues = stageDim.values.map(v => (v.value || v).toLowerCase());
    
    const stageMatch = stageValues.some(s => 
      projectStage.includes(s) || s.includes(projectStage)
    );
    
    if (stageMatch) {
      totalScore += 20;
      matchDetails.push({ factor: 'Stage', matched: true, score: 20 });
    }
    totalWeight += 20;
  }

  // 4. Size/Capacity Match (weight: 15%)
  const sizeDim = getDimension('dealSize') || getDimension('capacity');
  if (sizeDim?.range && project.capacity) {
    const projectCapacity = parseFloat(project.capacity);
    const min = sizeDim.range.min || 0;
    const max = sizeDim.range.max || Infinity;
    
    if (projectCapacity >= min && projectCapacity <= max) {
      totalScore += 15;
      matchDetails.push({ factor: 'Size', matched: true, score: 15 });
    }
    totalWeight += 15;
  }

  // 5. Deal Structure Match (weight: 10%)
  const structureDim = getDimension('dealStructure');
  if (structureDim?.values?.length > 0) {
    // Assume project acquisition type from status
    totalScore += 5; // Partial score
    totalWeight += 10;
  }

  // Calculate final percentage
  const matchPercent = totalWeight > 0 ? Math.round((totalScore / totalWeight) * 100) : 0;
  
  return {
    score: matchPercent,
    details: matchDetails,
    project
  };
}

// ============================================
// SUBCOMPONENTS
// ============================================

// Confidence Bar
const ConfidenceBar = ({ value, size = 'md' }) => {
  const width = Math.max(0, Math.min(100, value * 100));
  const color = value >= 0.8 ? 'bg-emerald-500' : value >= 0.5 ? 'bg-amber-500' : 'bg-slate-300';
  const height = size === 'sm' ? 'h-1.5' : 'h-2';
  
  return (
    <div className={`flex items-center gap-2 ${size === 'sm' ? 'w-20' : 'w-32'}`}>
      <div className={`flex-1 ${height} bg-slate-100 rounded-full overflow-hidden`}>
        <div className={`${height} ${color} rounded-full transition-all`} style={{ width: `${width}%` }} />
      </div>
      <span className={`text-slate-500 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>{Math.round(value * 100)}%</span>
    </div>
  );
};

// Technology Row
const TechnologyRow = ({ tech, index }) => {
  const isVerified = tech.source === 'both' || tech.source === 'revealed';
  const hasDeals = tech.deals > 0;
  
  return (
    <tr className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
      <td className="py-3 pr-4">
        <div>
          <span className="font-medium text-slate-900">{tech.value}</span>
          {tech.note && (
            <p className="text-xs text-amber-600 mt-0.5">{tech.note}</p>
          )}
        </div>
      </td>
      <td className="py-3 px-4 text-center">
        {isVerified ? (
          <CheckCircle size={18} className="text-emerald-500 mx-auto" />
        ) : (
          <MinusCircle size={18} className="text-slate-300 mx-auto" />
        )}
      </td>
      <td className="py-3 px-4 text-center text-slate-600">
        {hasDeals ? tech.deals : '—'}
      </td>
      <td className="py-3 px-4 text-center text-slate-600">
        {tech.capacity ? tech.capacity.toLocaleString() : '—'}
      </td>
      <td className="py-3 px-4">
        <ConfidenceBar value={tech.confidence || 0} size="sm" />
      </td>
    </tr>
  );
};

// Collapsible Dimension Card
const DimensionCard = ({ title, subtitle, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors"
      >
        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
          <Icon size={20} className="text-emerald-600" />
        </div>
        <div className="flex-1 text-left">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
        {isOpen ? (
          <ChevronDown size={20} className="text-slate-400" />
        ) : (
          <ChevronRight size={20} className="text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-slate-100">
          {children}
        </div>
      )}
    </div>
  );
};

// Project Match Card
const ProjectMatchCard = ({ matches, onLinkProject }) => {
  if (!matches || matches.length === 0) return null;
  
  return (
    <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl p-4 text-white">
      <div className="flex items-center gap-2 mb-3">
        <Target size={18} />
        <h3 className="font-semibold">Project Match</h3>
      </div>
      <p className="text-emerald-100 text-sm mb-3">
        Based on preferences, strong fit for:
      </p>
      <div className="space-y-2">
        {matches.slice(0, 3).map((match, i) => (
          <div 
            key={match.project.id}
            className="bg-white/10 backdrop-blur rounded-lg p-3 hover:bg-white/20 transition-colors cursor-pointer"
            onClick={() => onLinkProject?.(match.project.id)}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium">{match.project.name}</span>
              <span className="text-lg font-bold">{match.score}%</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-emerald-200 text-sm">
                {match.project.capacity && `${match.project.capacity} MW • `}
                {match.project.location?.split(',')[0]} • {match.project.stage}
              </span>
              <span className="text-emerald-200 text-xs">match</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Investment Profile Sidebar
const InvestmentProfile = ({ preferences }) => {
  if (!preferences) return null;
  
  // Calculate overall confidence
  const confidences = preferences.dimensions
    ?.flatMap(d => d.values?.map(v => v.confidence) || [])
    .filter(c => c != null) || [];
  const avgConfidence = confidences.length > 0 
    ? confidences.reduce((a, b) => a + b, 0) / confidences.length 
    : 0;
  
  // Get primary focus (highest confidence items)
  const techDim = preferences.dimensions?.find(d => 
    d.key === 'technologies' || d.key === 'sectors'
  );
  const primaryFocus = techDim?.values
    ?.filter(v => v.confidence >= 0.7)
    .slice(0, 3)
    .map(v => v.value) || [];
  
  // Get strategic notes
  const strategicNote = preferences.strategicNotes?.[0] || preferences.summary?.split('.')[0];
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold text-slate-900 mb-4">Investment Profile</h3>
      
      {/* Overall Confidence */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-slate-600">Overall Confidence</span>
          <span className="text-sm font-medium text-slate-900">{Math.round(avgConfidence * 100)}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-2 bg-emerald-500 rounded-full transition-all" 
            style={{ width: `${avgConfidence * 100}%` }} 
          />
        </div>
      </div>
      
      {/* Primary Focus */}
      {primaryFocus.length > 0 && (
        <div className="mb-4">
          <span className="text-sm text-slate-600 block mb-2">Primary Focus</span>
          <div className="flex flex-wrap gap-2">
            {primaryFocus.map((focus, i) => (
              <span key={i} className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium">
                {focus}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Strategic Note */}
      {strategicNote && (
        <div>
          <span className="text-sm text-slate-600 block mb-1">Strategic Shift</span>
          <p className="text-sm text-slate-700">{strategicNote}</p>
        </div>
      )}
    </div>
  );
};

// Deal History Card with expandable deals
const DealHistory = ({ deals }) => {
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [showAllDeals, setShowAllDeals] = useState(false);
  
  if (!deals || deals.length === 0) return null;
  
  const getIcon = (type) => {
    if (type?.includes('acqui') || type?.includes('purchase')) return TrendingUp;
    if (type?.includes('sale') || type?.includes('divest')) return ArrowUpRight;
    return TrendingUp;
  };

  const getDealTypeLabel = (type) => {
    if (!type) return 'Deal';
    if (type.includes('acqui')) return 'Acquisition';
    if (type.includes('sale') || type.includes('divest')) return 'Divestiture';
    if (type.includes('invest')) return 'Investment';
    if (type.includes('partner') || type.includes('jv')) return 'Partnership';
    if (type.includes('launch')) return 'Launch';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const DealItem = ({ deal, index, isExpanded, onToggle }) => {
    const Icon = getIcon(deal.type);
    return (
      <div className="border-b border-slate-100 last:border-0">
        <button
          onClick={() => onToggle(isExpanded ? null : index)}
          className="w-full flex items-start gap-3 p-3 hover:bg-slate-50 transition-colors text-left"
        >
          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <Icon size={16} className="text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-medium text-slate-900 text-sm ${isExpanded ? '' : 'truncate'}`}>{deal.description}</p>
            <p className="text-xs text-slate-500 truncate">
              {deal.location && `${deal.location} • `}
              {deal.capacity && `${deal.capacity} • `}
              {deal.counterparty && `${deal.type === 'acquisition' ? 'from' : 'to'} ${deal.counterparty}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-slate-400">{deal.date}</span>
            <ChevronDown size={14} className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </button>
        
        {isExpanded && (
          <div className="px-3 pb-3 ml-11 space-y-3 bg-slate-50 rounded-b-lg mx-3 mb-2">
            {/* Full Description */}
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Description</p>
              <p className="text-sm text-slate-700">{deal.description}</p>
            </div>
            
            {/* Deal Details Grid */}
            <div className="grid grid-cols-2 gap-3">
              {deal.type && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Type</p>
                  <span className="inline-flex px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                    {getDealTypeLabel(deal.type)}
                  </span>
                </div>
              )}
              {deal.value && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Value</p>
                  <p className="text-sm font-semibold text-slate-900">{deal.value}</p>
                </div>
              )}
              {deal.location && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Location</p>
                  <p className="text-sm text-slate-700">{deal.location}</p>
                </div>
              )}
              {deal.capacity && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Capacity</p>
                  <p className="text-sm text-slate-700">{deal.capacity}</p>
                </div>
              )}
              {deal.counterparty && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Counterparty</p>
                  <p className="text-sm text-slate-700">{deal.counterparty}</p>
                </div>
              )}
              {deal.date && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Date</p>
                  <p className="text-sm text-slate-700">{deal.date}</p>
                </div>
              )}
            </div>
            
            {/* Source Link */}
            {deal.source && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Source</p>
                <a 
                  href={deal.source} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                >
                  <ExternalLink size={12} /> View source
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-900">Deal History</h3>
            <p className="text-xs text-slate-500">{deals.length} deal{deals.length !== 1 ? 's' : ''} on record</p>
          </div>
          {deals.length > 4 && (
            <button 
              onClick={() => setShowAllDeals(true)}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              View All
            </button>
          )}
        </div>
        <div>
          {deals.slice(0, 4).map((deal, i) => (
            <DealItem 
              key={i} 
              deal={deal} 
              index={i} 
              isExpanded={expandedDeal === i}
              onToggle={setExpandedDeal}
            />
          ))}
        </div>
      </div>

      {/* View All Deals Modal */}
      {showAllDeals && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">All Deals</h2>
                <p className="text-sm text-slate-500">{deals.length} deals on record</p>
              </div>
              <button 
                onClick={() => setShowAllDeals(false)}
                className="p-2 hover:bg-slate-100 rounded-xl text-slate-500"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {deals.map((deal, i) => (
                <DealItem 
                  key={i} 
                  deal={deal} 
                  index={i + 100} 
                  isExpanded={expandedDeal === i + 100}
                  onToggle={setExpandedDeal}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Empty State
const EmptyState = ({ counterpart, onEnrich, onManualCreate, loading, error, isAdmin }) => (
  <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-8">
    <div className="text-center max-w-md mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
        <Sparkles size={28} className="text-emerald-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">No investment preferences yet</h3>
      <p className="text-slate-500 mb-6">
        {isAdmin
          ? "Preferences can be extracted from this company's website and public sources"
          : "Investment preferences will be automatically enriched by the system"}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {isAdmin && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={onEnrich}
            disabled={loading}
            className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <Sparkles size={18} />
                Extract with AI
              </>
            )}
          </button>
          <button
            onClick={onManualCreate}
            className="px-5 py-2.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl font-medium flex items-center gap-2 transition-colors"
          >
            <Edit2 size={18} />
            Enter Manually
          </button>
        </div>
      )}
    </div>
  </div>
);

// Enriched Header Banner
const EnrichedBanner = ({ metadata, onRefresh, onViewSources, loading, isAdmin }) => {
  const sourceCount = metadata?.sourceCount || 0;
  const lastUpdated = metadata?.lastEnriched
    ? formatTimeAgo(new Date(metadata.lastEnriched))
    : 'Unknown';

  return (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Sparkles size={20} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-900">Investment Preferences Auto-Enriched</h3>
            <p className="text-sm text-emerald-700">
              Based on {sourceCount} source{sourceCount !== 1 ? 's' : ''} • Last updated {lastUpdated}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onViewSources}
            className="text-sm text-emerald-700 hover:text-emerald-800 font-medium flex items-center gap-1"
          >
            View Sources
          </button>
          {isAdmin && (
            <button
              onClick={onRefresh}
              disabled={loading}
              className="p-2 hover:bg-emerald-100 rounded-lg text-emerald-600 transition-colors"
              title="Re-enrich"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function
function formatTimeAgo(date) {
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

// ============================================
// PREFERENCES EDITOR (Manual Entry)
// ============================================

const PreferencesEditor = ({ preferences, industryConfig, onSave, onCancel }) => {
  const [editedPrefs, setEditedPrefs] = useState(preferences || {
    industry: industryConfig?.id || 'custom',
    summary: '',
    dimensions: industryConfig?.dimensions?.map(d => ({
      key: d.key,
      label: d.label,
      type: d.type,
      values: []
    })) || []
  });

  const handleDimensionChange = (dimKey, values) => {
    setEditedPrefs(prev => ({
      ...prev,
      dimensions: prev.dimensions.map(d => 
        d.key === dimKey ? { ...d, values } : d
      )
    }));
  };

  const handleSave = () => {
    onSave({
      ...editedPrefs,
      metadata: {
        lastEnriched: new Date().toISOString(),
        sourceCount: 0,
        sources: [],
        manual: true
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Edit Investment Preferences</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium flex items-center gap-1"
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>

      {/* Summary */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Investment Summary</label>
        <textarea
          value={editedPrefs.summary || ''}
          onChange={(e) => setEditedPrefs(prev => ({ ...prev, summary: e.target.value }))}
          rows={2}
          placeholder="Brief description of investment focus and strategy..."
          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500/50"
        />
      </div>

      {/* Dimensions */}
      {industryConfig?.dimensions?.map(dimConfig => {
        const currentDim = editedPrefs.dimensions?.find(d => d.key === dimConfig.key);
        
        if (dimConfig.type === 'multi-select') {
          return (
            <div key={dimConfig.key}>
              <label className="block text-sm font-medium text-slate-700 mb-2">{dimConfig.label}</label>
              <div className="flex flex-wrap gap-2">
                {dimConfig.options?.map(opt => {
                  const isSelected = currentDim?.values?.some(v => v.value === opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        const newValues = isSelected
                          ? currentDim.values.filter(v => v.value !== opt)
                          : [...(currentDim?.values || []), { value: opt, confidence: 0.8, source: 'stated' }];
                        handleDimensionChange(dimConfig.key, newValues);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        isSelected 
                          ? "bg-emerald-500 text-white" 
                          : "bg-white border border-slate-200 text-slate-700 hover:border-emerald-300"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        
        if (dimConfig.type === 'range') {
          const range = currentDim?.range || {};
          return (
            <div key={dimConfig.key}>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {dimConfig.label} ({dimConfig.unit})
              </label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Min</label>
                  <input
                    type="number"
                    value={range.min || ''}
                    onChange={(e) => handleDimensionChange(dimConfig.key, [], {
                      ...currentDim,
                      range: { ...range, min: parseFloat(e.target.value) || null }
                    })}
                    placeholder="0"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Max</label>
                  <input
                    type="number"
                    value={range.max || ''}
                    onChange={(e) => handleDimensionChange(dimConfig.key, [], {
                      ...currentDim,
                      range: { ...range, max: parseFloat(e.target.value) || null }
                    })}
                    placeholder="No limit"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Sweet Spot</label>
                  <input
                    type="text"
                    value={range.sweetSpot || ''}
                    onChange={(e) => handleDimensionChange(dimConfig.key, [], {
                      ...currentDim,
                      range: { ...range, sweetSpot: e.target.value }
                    })}
                    placeholder="e.g., 50-100"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>
          );
        }
        
        return null;
      })}
    </div>
  );
};

// ============================================
// SOURCES MODAL
// ============================================

const SourcesModal = ({ sources, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Data Sources</h2>
        <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
          <X size={20} className="text-slate-500" />
        </button>
      </div>
      <div className="p-4 overflow-y-auto">
        {sources?.length > 0 ? (
          <div className="space-y-3">
            {sources.map((source, i) => (
              <div key={i} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  {source.type === 'website' && <Globe size={16} className="text-blue-500" />}
                  {source.type === 'search' && <Eye size={16} className="text-purple-500" />}
                  <span className="font-medium text-slate-900 capitalize">{source.type}</span>
                </div>
                <p className="text-sm text-slate-600 truncate">{source.url}</p>
                {source.fetchedAt && (
                  <p className="text-xs text-slate-400 mt-1">
                    Fetched {formatTimeAgo(new Date(source.fetchedAt))}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-center py-4">No source information available</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

export function InvestmentPreferences({ counterpart, currentIndustry, onUpdate, projects = [], isAdmin = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showSources, setShowSources] = useState(false);
  
  const prefs = counterpart.investmentPreferences;
  const industryConfig = getIndustryConfig(prefs?.industry || currentIndustry || 'custom');

  // Calculate project matches
  const projectMatches = useMemo(() => {
    if (!prefs || !projects?.length) return [];
    
    return projects
      .map(project => calculateProjectMatch(project, prefs, industryConfig))
      .filter(match => match && match.score >= 40)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [prefs, projects, industryConfig]);

  const handleEnrich = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let websiteUrl = counterpart.websiteUrl;
      
      // If no URL, prompt user to enter one
      if (!websiteUrl) {
        // Suggest a URL based on company name
        const cleanName = counterpart.name
          .toLowerCase()
          .replace(/\s+(inc|llc|ltd|corp|co|group|partners|capital|management|investments|asset|holdings)\.?$/gi, '')
          .replace(/[^a-z0-9]/g, '')
          .trim();
        const suggestedUrl = cleanName ? `https://www.${cleanName}.com` : '';
        
        const userInput = prompt(
          `Enter the company website URL for better results:\n\n(Leave empty to use AI knowledge only)`,
          suggestedUrl
        );
        
        // User cancelled
        if (userInput === null) {
          setLoading(false);
          return;
        }
        
        // User entered or accepted URL
        if (userInput.trim()) {
          websiteUrl = userInput.trim();
          // Add https if missing
          if (!websiteUrl.startsWith('http')) {
            websiteUrl = 'https://' + websiteUrl;
          }
        }
      }
      
      const response = await authFetch('/api/enrich-counterpart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          counterpartId: counterpart.id,
          name: counterpart.name,
          websiteUrl: websiteUrl,
          linkedinUrl: counterpart.linkedinUrl,
          industry: currentIndustry,
          existingPreferences: prefs,
          existingDeals: []
        })
      });
      
      if (!response.ok) {
        throw new Error('Enrichment request failed');
      }
      
      const data = await response.json();
      
      if (data.success && data.investmentPreferences) {
        // Build update object
        const updateObj = { 
          ...counterpart, 
          investmentPreferences: data.investmentPreferences 
        };
        
        // Save the URL that worked (guessed or from AI)
        if (!counterpart.websiteUrl) {
          if (data.investmentPreferences.companyInfo?.website) {
            updateObj.websiteUrl = data.investmentPreferences.companyInfo.website;
          } else if (websiteUrl) {
            // Save the guessed URL since enrichment succeeded
            updateObj.websiteUrl = websiteUrl;
          }
        }
        
        // Update company type/category if AI detected it
        if (data.investmentPreferences.companyType) {
          updateObj.category = data.investmentPreferences.companyType;
        }
        
        // Update company info fields if available
        const companyInfo = data.investmentPreferences.companyInfo;
        if (companyInfo) {
          if (companyInfo.employees) updateObj.employees = companyInfo.employees;
          if (companyInfo.founded) updateObj.founded = companyInfo.founded;
          if (companyInfo.headquarters) updateObj.location = companyInfo.headquarters;
          if (companyInfo.pipeline) updateObj.pipeline = companyInfo.pipeline;
          if (companyInfo.aum) updateObj.aum = companyInfo.aum;
        }

        // Update headquarters from new structured field
        const hq = data.investmentPreferences.headquarters;
        console.log('[InvestmentPreferences] Headquarters from API:', hq);
        if (hq && (hq.city || hq.country)) {
          updateObj.headquarters = hq;
          console.log('[InvestmentPreferences] Setting headquarters:', hq);
          // Also set location as fallback string for backwards compatibility
          if (!updateObj.location) {
            updateObj.location = [hq.city, hq.country].filter(Boolean).join(', ');
          }
        }
        
        // Update summary if available (always update, AI has latest info)
        if (data.investmentPreferences.summary) {
          updateObj.summary = data.investmentPreferences.summary;
        }
        
        onUpdate(updateObj);
      } else {
        throw new Error(data.message || 'Unknown error');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [counterpart, currentIndustry, prefs, onUpdate]);

  const handleSaveEdit = useCallback((updatedPrefs) => {
    onUpdate({ ...counterpart, investmentPreferences: updatedPrefs });
    setEditMode(false);
  }, [counterpart, onUpdate]);

  const handleClearPreferences = useCallback(() => {
    if (window.confirm('Clear all investment preferences? This cannot be undone.')) {
      onUpdate({ ...counterpart, investmentPreferences: null });
    }
  }, [counterpart, onUpdate]);

  // Edit mode - check first
  if (editMode) {
    return (
      <PreferencesEditor
        preferences={prefs}
        industryConfig={industryConfig}
        onSave={handleSaveEdit}
        onCancel={() => setEditMode(false)}
      />
    );
  }

  // Empty state
  if (!prefs) {
    return (
      <EmptyState
        counterpart={counterpart}
        onEnrich={handleEnrich}
        onManualCreate={() => setEditMode(true)}
        loading={loading}
        error={error}
        isAdmin={isAdmin}
      />
    );
  }

  // Get technologies dimension for main display
  const techDim = prefs.dimensions?.find(d => 
    d.key === 'technologies' || d.key === 'sectors' || d.key === 'assetClasses'
  );
  const geoDim = prefs.dimensions?.find(d => d.key === 'geographies');
  const otherDims = prefs.dimensions?.filter(d => 
    d.key !== 'technologies' && d.key !== 'sectors' && d.key !== 'assetClasses' && d.key !== 'geographies'
  );

  // Count stats for geography subtitle
  const geoStats = geoDim?.values?.reduce((acc, g) => {
    const status = g.status || 'active';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {}) || {};
  const geoSubtitle = Object.entries(geoStats)
    .map(([status, count]) => `${count} ${status}`)
    .join(' • ');

  return (
    <div className="space-y-6">
      {/* Enriched Banner */}
      {prefs.metadata && (
        <EnrichedBanner
          metadata={prefs.metadata}
          onRefresh={handleEnrich}
          onViewSources={() => setShowSources(true)}
          loading={loading}
          isAdmin={isAdmin}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Dimensions */}
        <div className="lg:col-span-2 space-y-4">
          {/* Technologies */}
          {techDim && techDim.values?.length > 0 && (
            <DimensionCard
              title="Technologies"
              subtitle={`${techDim.values.filter(v => v.source === 'both' || v.source === 'revealed').length} stated • ${techDim.values.filter(v => v.deals > 0).length} with deal activity`}
              icon={Zap}
              defaultOpen={true}
            >
              <table className="w-full mt-3">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase tracking-wider">
                    <th className="text-left py-2 pr-4">Technology</th>
                    <th className="text-center py-2 px-4">Verified</th>
                    <th className="text-center py-2 px-4">Deals</th>
                    <th className="text-center py-2 px-4">{industryConfig?.dimensions?.[0]?.unit || 'MW'}</th>
                    <th className="text-left py-2 px-4">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {techDim.values.map((tech, i) => (
                    <TechnologyRow key={tech.value || i} tech={tech} index={i} />
                  ))}
                </tbody>
              </table>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <CheckCircle size={14} className="text-emerald-500" /> Stated + Deals
                </span>
                <span className="flex items-center gap-1">
                  <MinusCircle size={14} className="text-slate-300" /> Stated only
                </span>
              </div>
            </DimensionCard>
          )}

          {/* Geographies */}
          {geoDim && geoDim.values?.length > 0 && (
            <DimensionCard
              title="Geographies"
              subtitle={geoSubtitle}
              icon={Globe}
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                {geoDim.values.map((geo, i) => (
                  <div 
                    key={geo.value || geo.region || i}
                    className={`p-2 rounded-lg text-sm ${
                      geo.status === 'expanding' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                      geo.status === 'active' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                      geo.status === 'selective' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                      geo.status === 'divesting' ? 'bg-red-50 text-red-700 border border-red-200' :
                      'bg-slate-50 text-slate-700 border border-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{geo.value || geo.region}</span>
                      <span className="text-xs capitalize opacity-75">{geo.status || 'active'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </DimensionCard>
          )}

          {/* Other Dimensions */}
          {otherDims?.filter(d => d.values?.length > 0 || d.range).map(dim => (
            <DimensionCard
              key={dim.key}
              title={dim.label}
              subtitle={dim.range ? `${dim.range.min || 0} - ${dim.range.max || '∞'} ${dim.range.unit || ''}` : `${dim.values?.length || 0} options`}
              icon={BarChart3}
            >
              {dim.range ? (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-slate-500">Min</p>
                      <p className="font-semibold text-slate-900">{dim.range.min || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Sweet Spot</p>
                      <p className="font-semibold text-emerald-600">{dim.range.sweetSpot || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Max</p>
                      <p className="font-semibold text-slate-900">{dim.range.max || '∞'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 mt-3">
                  {dim.values?.map((v, i) => (
                    <span 
                      key={v.value || i}
                      className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm"
                    >
                      {v.value}
                    </span>
                  ))}
                </div>
              )}
            </DimensionCard>
          ))}
        </div>

        {/* Right Column - Profile & Matches */}
        <div className="space-y-4">
          <InvestmentProfile preferences={prefs} />
          <ProjectMatchCard 
            matches={projectMatches} 
            onLinkProject={(projectId) => {
              // TODO: Implement link to project action
              console.log('Link to project:', projectId);
            }}
          />
          
          {/* Actions - Admin only */}
          {isAdmin && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditMode(true)}
                  className="flex-1 px-3 py-2 text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Edit2 size={16} /> Edit
                </button>
                <button
                  onClick={handleClearPreferences}
                  className="px-3 py-2 text-red-500 hover:bg-red-50 border border-red-200 rounded-lg text-sm font-medium"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sources Modal */}
      {showSources && (
        <SourcesModal
          sources={prefs.metadata?.sources}
          onClose={() => setShowSources(false)}
        />
      )}
    </div>
  );
}

export default InvestmentPreferences;
